/**
 * Messages Domain Seed Data
 *
 * Contains comprehensive message thread data for:
 * - Case-related communication between consumers and staff
 * - Support threads
 * - Loan status updates
 * - Document requests and confirmations
 *
 * This provides rich historical data for the Messages view in Consumer Portal
 * and the communication features in Business Portal.
 */

import { seedId } from '../utils';
import { CASE_IDS } from './cases';
import { CONSUMER_IDS } from './consumers';
import { BUSINESS_IDS } from './tenants';
import { USER_IDS } from './users';

// =============================================================================
// MESSAGE THREAD IDs
// =============================================================================

export const MESSAGE_THREAD_IDS = {
  // Marcus Thompson Threads
  MARCUS_INSURANCE_THREAD: seedId('thread-marcus-insurance', 1),
  MARCUS_PORTFOLIO_THREAD: seedId('thread-marcus-portfolio', 2),
  MARCUS_GENERAL_THREAD: seedId('thread-marcus-general', 3),

  // Robert Chen Threads
  ROBERT_TAX_THREAD: seedId('thread-robert-tax', 1),
  ROBERT_WIRE_THREAD: seedId('thread-robert-wire', 2),

  // David Williams Threads
  DAVID_ACH_THREAD: seedId('thread-david-ach', 1),
  DAVID_SUPPORT_THREAD: seedId('thread-david-support', 2),

  // Alicia Fernandez Threads
  ALICIA_DSCR_THREAD: seedId('thread-alicia-dscr', 1),
  ALICIA_LEASE_THREAD: seedId('thread-alicia-lease', 2),

  // Jennifer Martinez Threads (New Borrower)
  JENNIFER_QUESTIONS_THREAD: seedId('thread-jennifer-questions', 1),
  JENNIFER_DOCS_THREAD: seedId('thread-jennifer-docs', 2),

  // Kevin Johnson Threads
  KEVIN_LOGIN_THREAD: seedId('thread-kevin-login', 1),
  KEVIN_PREQUAL_THREAD: seedId('thread-kevin-prequal', 2),

  // Maria Santos Threads (One Sharpe Capital)
  MARIA_LLC_THREAD: seedId('thread-maria-llc', 1),
  MARIA_PAYOFF_THREAD: seedId('thread-maria-payoff', 2),

  // Christopher Davis Threads (One Sharpe Capital)
  CHRIS_PHOTOS_THREAD: seedId('thread-chris-photos', 1),
  CHRIS_DRAW_THREAD: seedId('thread-chris-draw', 2),
} as const;

export type MessageThreadId = (typeof MESSAGE_THREAD_IDS)[keyof typeof MESSAGE_THREAD_IDS];

// =============================================================================
// MESSAGE IDs
// =============================================================================

export const MESSAGE_IDS = {
  // Marcus Insurance Thread
  MARCUS_INS_1: seedId('msg-marcus-ins', 1),
  MARCUS_INS_2: seedId('msg-marcus-ins', 2),
  MARCUS_INS_3: seedId('msg-marcus-ins', 3),
  MARCUS_INS_4: seedId('msg-marcus-ins', 4),
  MARCUS_INS_5: seedId('msg-marcus-ins', 5),

  // Marcus Portfolio Thread
  MARCUS_PORT_1: seedId('msg-marcus-port', 1),
  MARCUS_PORT_2: seedId('msg-marcus-port', 2),
  MARCUS_PORT_3: seedId('msg-marcus-port', 3),
  MARCUS_PORT_4: seedId('msg-marcus-port', 4),

  // Robert Tax Thread
  ROBERT_TAX_1: seedId('msg-robert-tax', 1),
  ROBERT_TAX_2: seedId('msg-robert-tax', 2),
  ROBERT_TAX_3: seedId('msg-robert-tax', 3),
  ROBERT_TAX_4: seedId('msg-robert-tax', 4),

  // Robert Wire Thread
  ROBERT_WIRE_1: seedId('msg-robert-wire', 1),
  ROBERT_WIRE_2: seedId('msg-robert-wire', 2),
  ROBERT_WIRE_3: seedId('msg-robert-wire', 3),

  // David ACH Thread
  DAVID_ACH_1: seedId('msg-david-ach', 1),
  DAVID_ACH_2: seedId('msg-david-ach', 2),
  DAVID_ACH_3: seedId('msg-david-ach', 3),
  DAVID_ACH_4: seedId('msg-david-ach', 4),
  DAVID_ACH_5: seedId('msg-david-ach', 5),
  DAVID_ACH_6: seedId('msg-david-ach', 6),

  // Alicia DSCR Thread
  ALICIA_DSCR_1: seedId('msg-alicia-dscr', 1),
  ALICIA_DSCR_2: seedId('msg-alicia-dscr', 2),
  ALICIA_DSCR_3: seedId('msg-alicia-dscr', 3),

  // Jennifer Questions Thread
  JENNIFER_Q_1: seedId('msg-jennifer-q', 1),
  JENNIFER_Q_2: seedId('msg-jennifer-q', 2),
  JENNIFER_Q_3: seedId('msg-jennifer-q', 3),
  JENNIFER_Q_4: seedId('msg-jennifer-q', 4),
  JENNIFER_Q_5: seedId('msg-jennifer-q', 5),

  // Jennifer Docs Thread
  JENNIFER_DOC_1: seedId('msg-jennifer-doc', 1),
  JENNIFER_DOC_2: seedId('msg-jennifer-doc', 2),
  JENNIFER_DOC_3: seedId('msg-jennifer-doc', 3),

  // Kevin Login Thread
  KEVIN_LOGIN_1: seedId('msg-kevin-login', 1),
  KEVIN_LOGIN_2: seedId('msg-kevin-login', 2),
  KEVIN_LOGIN_3: seedId('msg-kevin-login', 3),
  KEVIN_LOGIN_4: seedId('msg-kevin-login', 4),

  // Kevin Prequal Thread
  KEVIN_PREQUAL_1: seedId('msg-kevin-prequal', 1),
  KEVIN_PREQUAL_2: seedId('msg-kevin-prequal', 2),
  KEVIN_PREQUAL_3: seedId('msg-kevin-prequal', 3),

  // Maria LLC Thread
  MARIA_LLC_1: seedId('msg-maria-llc', 1),
  MARIA_LLC_2: seedId('msg-maria-llc', 2),
  MARIA_LLC_3: seedId('msg-maria-llc', 3),

  // Maria Payoff Thread
  MARIA_PAYOFF_1: seedId('msg-maria-payoff', 1),
  MARIA_PAYOFF_2: seedId('msg-maria-payoff', 2),
  MARIA_PAYOFF_3: seedId('msg-maria-payoff', 3),
  MARIA_PAYOFF_4: seedId('msg-maria-payoff', 4),

  // Chris Photos Thread
  CHRIS_PHOTOS_1: seedId('msg-chris-photos', 1),
  CHRIS_PHOTOS_2: seedId('msg-chris-photos', 2),
  CHRIS_PHOTOS_3: seedId('msg-chris-photos', 3),

  // Chris Draw Thread
  CHRIS_DRAW_1: seedId('msg-chris-draw', 1),
  CHRIS_DRAW_2: seedId('msg-chris-draw', 2),
  CHRIS_DRAW_3: seedId('msg-chris-draw', 3),
  CHRIS_DRAW_4: seedId('msg-chris-draw', 4),
} as const;

export type MessageId = (typeof MESSAGE_IDS)[keyof typeof MESSAGE_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface MessageThreadSeedData {
  id: string;
  businessId: string;
  consumerId: string;
  caseId?: string;
  subject: string;
  status: 'active' | 'resolved' | 'archived';
  unreadCount: number;
  lastMessageAt: number; // days ago
}

export interface MessageSeedData {
  id: string;
  businessId: string;
  threadId: string;
  senderId: string;
  senderType: 'consumer' | 'user' | 'system';
  senderName: string;
  senderEmail?: string;
  content: string;
  isRead: boolean;
  daysAgo: number;
  hoursOffset?: number; // hours within the day
}

// =============================================================================
// MESSAGE THREAD DATA
// =============================================================================

export const MESSAGE_THREADS: Record<keyof typeof MESSAGE_THREAD_IDS, MessageThreadSeedData> = {
  // Marcus Thompson Threads
  MARCUS_INSURANCE_THREAD: {
    id: MESSAGE_THREAD_IDS.MARCUS_INSURANCE_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    caseId: CASE_IDS.MARCUS_INSURANCE_CERT,
    subject: 'Insurance Certificate Request',
    status: 'active',
    unreadCount: 0,
    lastMessageAt: 2,
  },
  MARCUS_PORTFOLIO_THREAD: {
    id: MESSAGE_THREAD_IDS.MARCUS_PORTFOLIO_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    caseId: CASE_IDS.MARCUS_PORTFOLIO_EXPANSION,
    subject: '5 Property Package - Pre-Approval',
    status: 'active',
    unreadCount: 1,
    lastMessageAt: 1,
  },
  MARCUS_GENERAL_THREAD: {
    id: MESSAGE_THREAD_IDS.MARCUS_GENERAL_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.MARCUS_THOMPSON,
    subject: 'General Questions',
    status: 'resolved',
    unreadCount: 0,
    lastMessageAt: 15,
  },

  // Robert Chen Threads
  ROBERT_TAX_THREAD: {
    id: MESSAGE_THREAD_IDS.ROBERT_TAX_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    caseId: CASE_IDS.ROBERT_TAX_UPLOAD,
    subject: 'Tax Return Upload Issue',
    status: 'active',
    unreadCount: 0,
    lastMessageAt: 4,
  },
  ROBERT_WIRE_THREAD: {
    id: MESSAGE_THREAD_IDS.ROBERT_WIRE_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ROBERT_CHEN,
    caseId: CASE_IDS.ROBERT_WIRE_CONFIRM,
    subject: 'Wire Transfer Confirmation',
    status: 'resolved',
    unreadCount: 0,
    lastMessageAt: 2,
  },

  // David Williams Threads
  DAVID_ACH_THREAD: {
    id: MESSAGE_THREAD_IDS.DAVID_ACH_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    caseId: CASE_IDS.DAVID_ACH_FAILED,
    subject: 'ACH Payment Issue - Urgent',
    status: 'resolved',
    unreadCount: 0,
    lastMessageAt: 1,
  },
  DAVID_SUPPORT_THREAD: {
    id: MESSAGE_THREAD_IDS.DAVID_SUPPORT_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.DAVID_WILLIAMS,
    caseId: CASE_IDS.DAVID_ADD_USER,
    subject: 'Add Authorized User',
    status: 'resolved',
    unreadCount: 0,
    lastMessageAt: 10,
  },

  // Alicia Fernandez Threads
  ALICIA_DSCR_THREAD: {
    id: MESSAGE_THREAD_IDS.ALICIA_DSCR_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    caseId: CASE_IDS.ALICIA_DSCR_RATES,
    subject: 'DSCR Loan Rates Inquiry',
    status: 'active',
    unreadCount: 1,
    lastMessageAt: 0,
  },
  ALICIA_LEASE_THREAD: {
    id: MESSAGE_THREAD_IDS.ALICIA_LEASE_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    caseId: CASE_IDS.ALICIA_LEASE_UPDATES,
    subject: 'Lease Agreement Updates',
    status: 'resolved',
    unreadCount: 0,
    lastMessageAt: 5,
  },

  // Jennifer Martinez Threads
  JENNIFER_QUESTIONS_THREAD: {
    id: MESSAGE_THREAD_IDS.JENNIFER_QUESTIONS_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    caseId: CASE_IDS.JENNIFER_FIRST_TIME,
    subject: 'First-Time Borrower Questions',
    status: 'resolved',
    unreadCount: 0,
    lastMessageAt: 7,
  },
  JENNIFER_DOCS_THREAD: {
    id: MESSAGE_THREAD_IDS.JENNIFER_DOCS_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    caseId: CASE_IDS.JENNIFER_DOC_CHECKLIST,
    subject: 'Document Checklist Help',
    status: 'active',
    unreadCount: 1,
    lastMessageAt: 1,
  },

  // Kevin Johnson Threads
  KEVIN_LOGIN_THREAD: {
    id: MESSAGE_THREAD_IDS.KEVIN_LOGIN_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.KEVIN_JOHNSON,
    caseId: CASE_IDS.KEVIN_LOGIN_ISSUES,
    subject: 'Portal Login Issues',
    status: 'resolved',
    unreadCount: 0,
    lastMessageAt: 3,
  },
  KEVIN_PREQUAL_THREAD: {
    id: MESSAGE_THREAD_IDS.KEVIN_PREQUAL_THREAD,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    consumerId: CONSUMER_IDS.KEVIN_JOHNSON,
    caseId: CASE_IDS.KEVIN_PREQUALIFICATION,
    subject: 'Pre-Qualification Request',
    status: 'active',
    unreadCount: 0,
    lastMessageAt: 1,
  },

  // Maria Santos Threads (One Sharpe)
  MARIA_LLC_THREAD: {
    id: MESSAGE_THREAD_IDS.MARIA_LLC_THREAD,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    caseId: CASE_IDS.MARIA_LLC_DOCS,
    subject: 'New LLC Documentation',
    status: 'active',
    unreadCount: 0,
    lastMessageAt: 2,
  },
  MARIA_PAYOFF_THREAD: {
    id: MESSAGE_THREAD_IDS.MARIA_PAYOFF_THREAD,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.MARIA_SANTOS,
    caseId: CASE_IDS.MARIA_PAYOFF_QUOTE,
    subject: 'Payoff Quote Request',
    status: 'active',
    unreadCount: 0,
    lastMessageAt: 1,
  },

  // Christopher Davis Threads (One Sharpe)
  CHRIS_PHOTOS_THREAD: {
    id: MESSAGE_THREAD_IDS.CHRIS_PHOTOS_THREAD,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    caseId: CASE_IDS.CHRISTOPHER_PHOTOS,
    subject: 'Renovation Photos Submission',
    status: 'active',
    unreadCount: 0,
    lastMessageAt: 1,
  },
  CHRIS_DRAW_THREAD: {
    id: MESSAGE_THREAD_IDS.CHRIS_DRAW_THREAD,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    consumerId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    caseId: CASE_IDS.CHRISTOPHER_DRAW,
    subject: 'Final Draw Request',
    status: 'active',
    unreadCount: 1,
    lastMessageAt: 0,
  },
};

// =============================================================================
// MESSAGE DATA
// =============================================================================

export const MESSAGES: Record<keyof typeof MESSAGE_IDS, MessageSeedData> = {
  // ============================================================
  // MARCUS INSURANCE THREAD
  // ============================================================
  MARCUS_INS_1: {
    id: MESSAGE_IDS.MARCUS_INS_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_INSURANCE_THREAD,
    senderId: CONSUMER_IDS.MARCUS_THOMPSON,
    senderType: 'consumer',
    senderName: 'Marcus Thompson',
    senderEmail: 'marcus.thompson@investorpro.com',
    content:
      "Hi, I received a notice that my insurance certificate for 1234 Oak Valley Dr has expired. I'm reaching out to my insurance provider to get an updated one. What's the deadline for submitting this?",
    isRead: true,
    daysAgo: 5,
    hoursOffset: 9,
  },
  MARCUS_INS_2: {
    id: MESSAGE_IDS.MARCUS_INS_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_INSURANCE_THREAD,
    senderId: USER_IDS.RACHEL_MARTINEZ,
    senderType: 'user',
    senderName: 'Rachel Martinez',
    senderEmail: 'rachel.martinez@opsmatrix.local',
    content:
      'Hi Marcus! Thanks for reaching out so quickly. We need the updated certificate within 10 business days to maintain compliance. Please ensure the new certificate shows coverage through at least your loan maturity date (December 2026). Let me know if you need anything else!',
    isRead: true,
    daysAgo: 5,
    hoursOffset: 11,
  },
  MARCUS_INS_3: {
    id: MESSAGE_IDS.MARCUS_INS_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_INSURANCE_THREAD,
    senderId: CONSUMER_IDS.MARCUS_THOMPSON,
    senderType: 'consumer',
    senderName: 'Marcus Thompson',
    senderEmail: 'marcus.thompson@investorpro.com',
    content:
      "Perfect, thank you Rachel. My agent said they can have the updated certificate ready by end of week. I'll upload it as soon as I receive it.",
    isRead: true,
    daysAgo: 4,
    hoursOffset: 14,
  },
  MARCUS_INS_4: {
    id: MESSAGE_IDS.MARCUS_INS_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_INSURANCE_THREAD,
    senderId: CONSUMER_IDS.MARCUS_THOMPSON,
    senderType: 'consumer',
    senderName: 'Marcus Thompson',
    senderEmail: 'marcus.thompson@investorpro.com',
    content:
      'Just uploaded the new insurance certificate. Coverage is through January 2027, so we should be good for the full loan term. Please confirm receipt when you have a chance.',
    isRead: true,
    daysAgo: 3,
    hoursOffset: 10,
  },
  MARCUS_INS_5: {
    id: MESSAGE_IDS.MARCUS_INS_5,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_INSURANCE_THREAD,
    senderId: USER_IDS.RACHEL_MARTINEZ,
    senderType: 'user',
    senderName: 'Rachel Martinez',
    senderEmail: 'rachel.martinez@opsmatrix.local',
    content:
      "Got it! I've reviewed and approved the certificate. Coverage looks great through January 2027. Your file is now fully compliant. Thanks for taking care of this so promptly, Marcus!",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 15,
  },

  // ============================================================
  // MARCUS PORTFOLIO THREAD
  // ============================================================
  MARCUS_PORT_1: {
    id: MESSAGE_IDS.MARCUS_PORT_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_PORTFOLIO_THREAD,
    senderId: CONSUMER_IDS.MARCUS_THOMPSON,
    senderType: 'consumer',
    senderName: 'Marcus Thompson',
    senderEmail: 'marcus.thompson@investorpro.com',
    content:
      "Hi team, I've got a package deal on 5 rental properties in the Austin market. Total acquisition cost is around $2.5M. All properties are under contract with a 30-day close. Can we start the pre-approval process?",
    isRead: true,
    daysAgo: 3,
    hoursOffset: 8,
  },
  MARCUS_PORT_2: {
    id: MESSAGE_IDS.MARCUS_PORT_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_PORTFOLIO_THREAD,
    senderId: USER_IDS.RACHEL_MARTINEZ,
    senderType: 'user',
    senderName: 'Rachel Martinez',
    senderEmail: 'rachel.martinez@opsmatrix.local',
    content:
      "Marcus, great to hear about this opportunity! With your track record of 12 completed deals, you're definitely a strong candidate for portfolio financing. I'm pulling together the documentation requirements now. A few quick questions:\n\n1. Are these all single-family homes or a mix of property types?\n2. What's the estimated rental income for the portfolio?\n3. Are any of the properties already tenanted?\n\nOnce I have this info, I can get you preliminary terms within 24 hours.",
    isRead: true,
    daysAgo: 3,
    hoursOffset: 10,
  },
  MARCUS_PORT_3: {
    id: MESSAGE_IDS.MARCUS_PORT_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_PORTFOLIO_THREAD,
    senderId: CONSUMER_IDS.MARCUS_THOMPSON,
    senderType: 'consumer',
    senderName: 'Marcus Thompson',
    senderEmail: 'marcus.thompson@investorpro.com',
    content:
      'Great questions! Here are the details:\n\n1. Mix: 3 single-family homes and 2 duplexes\n2. Estimated gross monthly rent: $18,500\n3. Currently 3 of 7 units are tenanted with existing leases\n\nI can send over the pro forma and rent rolls. Looking forward to the preliminary terms!',
    isRead: true,
    daysAgo: 2,
    hoursOffset: 9,
  },
  MARCUS_PORT_4: {
    id: MESSAGE_IDS.MARCUS_PORT_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARCUS_PORTFOLIO_THREAD,
    senderId: USER_IDS.SARAH_CHEN,
    senderType: 'user',
    senderName: 'Sarah Chen',
    senderEmail: 'sarah.chen@opsmatrix.local',
    content:
      "Hi Marcus, Sarah here from underwriting. I've reviewed the initial numbers and the portfolio looks solid. With $18,500 monthly gross rent against a $2.5M loan, your DSCR should easily clear our 1.25x threshold.\n\nI'm preparing preliminary terms for Rachel to share with you. We should have something by end of day tomorrow. In the meantime, please upload the rent rolls and any existing leases to your portal.",
    isRead: false,
    daysAgo: 1,
    hoursOffset: 16,
  },

  // ============================================================
  // ROBERT TAX THREAD
  // ============================================================
  ROBERT_TAX_1: {
    id: MESSAGE_IDS.ROBERT_TAX_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ROBERT_TAX_THREAD,
    senderId: CONSUMER_IDS.ROBERT_CHEN,
    senderType: 'consumer',
    senderName: 'Robert Chen',
    senderEmail: 'robert.chen@chenenterprises.com',
    content:
      "I'm trying to upload my 2024 tax returns but keep getting an error. The files are about 45MB total (corporate + personal returns). Is there a file size limit I'm hitting?",
    isRead: true,
    daysAgo: 7,
    hoursOffset: 14,
  },
  ROBERT_TAX_2: {
    id: MESSAGE_IDS.ROBERT_TAX_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ROBERT_TAX_THREAD,
    senderId: USER_IDS.MIKE_JOHNSON,
    senderType: 'user',
    senderName: 'Mike Johnson',
    senderEmail: 'mike.johnson@opsmatrix.local',
    content:
      "Hi Robert, you're right - we have a 25MB upload limit by default. Given the size of your returns, I've increased your limit to 100MB. Please try uploading again and let me know if you run into any issues.\n\nAlternatively, if you prefer, you can email the documents directly to docs@opsmatrix.com and reference your account number.",
    isRead: true,
    daysAgo: 6,
    hoursOffset: 10,
  },
  ROBERT_TAX_3: {
    id: MESSAGE_IDS.ROBERT_TAX_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ROBERT_TAX_THREAD,
    senderId: CONSUMER_IDS.ROBERT_CHEN,
    senderType: 'consumer',
    senderName: 'Robert Chen',
    senderEmail: 'robert.chen@chenenterprises.com',
    content:
      'The upload worked this time - thank you for the quick fix! I just submitted both the corporate and personal returns. Please confirm when they show up on your end.',
    isRead: true,
    daysAgo: 5,
    hoursOffset: 11,
  },
  ROBERT_TAX_4: {
    id: MESSAGE_IDS.ROBERT_TAX_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ROBERT_TAX_THREAD,
    senderId: USER_IDS.JAMES_WILSON,
    senderType: 'user',
    senderName: 'James Wilson',
    senderEmail: 'james.wilson@opsmatrix.local',
    content:
      "Got them, Robert! Both tax returns have been received and are now in review. Our document analysis has extracted the key figures automatically - I'll have the review completed within 48 hours. Thanks for your patience with the upload issue!",
    isRead: true,
    daysAgo: 4,
    hoursOffset: 14,
  },

  // ============================================================
  // ROBERT WIRE THREAD
  // ============================================================
  ROBERT_WIRE_1: {
    id: MESSAGE_IDS.ROBERT_WIRE_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ROBERT_WIRE_THREAD,
    senderId: CONSUMER_IDS.ROBERT_CHEN,
    senderType: 'consumer',
    senderName: 'Robert Chen',
    senderEmail: 'robert.chen@chenenterprises.com',
    content:
      'I just sent a wire for the payoff on loan #LC-2024-0892. Wire reference number is FED20240115-7823. Can you please confirm receipt?',
    isRead: true,
    daysAgo: 3,
    hoursOffset: 9,
  },
  ROBERT_WIRE_2: {
    id: MESSAGE_IDS.ROBERT_WIRE_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ROBERT_WIRE_THREAD,
    senderId: USER_IDS.JAMES_WILSON,
    senderType: 'user',
    senderName: 'James Wilson',
    senderEmail: 'james.wilson@opsmatrix.local',
    content:
      "Hi Robert, I've confirmed receipt of your wire - $342,567.89 received and applied to loan #LC-2024-0892. The payoff has been processed and your lien release will be recorded within 5 business days.\n\nI'll send over the satisfaction of mortgage document once the recording is complete.",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 15,
  },
  ROBERT_WIRE_3: {
    id: MESSAGE_IDS.ROBERT_WIRE_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ROBERT_WIRE_THREAD,
    senderId: CONSUMER_IDS.ROBERT_CHEN,
    senderType: 'consumer',
    senderName: 'Robert Chen',
    senderEmail: 'robert.chen@chenenterprises.com',
    content:
      'Excellent, thank you James! That was a great loan - smooth process from start to finish. Looking forward to working with Fiops on the next deal.',
    isRead: true,
    daysAgo: 2,
    hoursOffset: 16,
  },

  // ============================================================
  // DAVID ACH THREAD (Urgent)
  // ============================================================
  DAVID_ACH_1: {
    id: MESSAGE_IDS.DAVID_ACH_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.DAVID_ACH_THREAD,
    senderId: CONSUMER_IDS.DAVID_WILLIAMS,
    senderType: 'consumer',
    senderName: 'David Williams',
    senderEmail: 'david.williams@williamsrealty.com',
    content:
      "URGENT: Just got a notice that my monthly payment was rejected. I recently changed banks and forgot to update the account info! The old account is closed. Please help - I don't want to incur late fees.",
    isRead: true,
    daysAgo: 3,
    hoursOffset: 8,
  },
  DAVID_ACH_2: {
    id: MESSAGE_IDS.DAVID_ACH_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.DAVID_ACH_THREAD,
    senderId: USER_IDS.MIKE_JOHNSON,
    senderType: 'user',
    senderName: 'Mike Johnson',
    senderEmail: 'mike.johnson@opsmatrix.local',
    content:
      "David, no worries - we can fix this quickly! I've flagged your account to prevent any late fee charges while we sort this out. Please provide:\n\n1. New bank name\n2. Routing number\n3. Account number\n4. A voided check or bank letter confirming the account\n\nOnce I have these, I can update your ACH info and retry the payment today.",
    isRead: true,
    daysAgo: 3,
    hoursOffset: 9,
  },
  DAVID_ACH_3: {
    id: MESSAGE_IDS.DAVID_ACH_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.DAVID_ACH_THREAD,
    senderId: CONSUMER_IDS.DAVID_WILLIAMS,
    senderType: 'consumer',
    senderName: 'David Williams',
    senderEmail: 'david.williams@williamsrealty.com',
    content:
      'Thank you Mike! Here are the details:\n\nBank: Chase Bank\nRouting: 111000614\nAccount: ****4521\n\nI just uploaded a voided check to my portal. Please process ASAP!',
    isRead: true,
    daysAgo: 3,
    hoursOffset: 10,
  },
  DAVID_ACH_4: {
    id: MESSAGE_IDS.DAVID_ACH_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.DAVID_ACH_THREAD,
    senderId: USER_IDS.JAMES_WILSON,
    senderType: 'user',
    senderName: 'James Wilson',
    senderEmail: 'james.wilson@opsmatrix.local',
    content:
      "Hi David, James here from payments. I've verified the voided check and updated your ACH information. The payment retry has been initiated and should process within 24 hours.\n\nI'll confirm once the payment clears. No late fees will be applied to your account.",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 11,
  },
  DAVID_ACH_5: {
    id: MESSAGE_IDS.DAVID_ACH_5,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.DAVID_ACH_THREAD,
    senderId: 'system',
    senderType: 'system',
    senderName: 'Payment System',
    content:
      'Payment Update: Your ACH payment of $3,847.22 has been successfully processed from account ending in 4521. Thank you!',
    isRead: true,
    daysAgo: 1,
    hoursOffset: 14,
  },
  DAVID_ACH_6: {
    id: MESSAGE_IDS.DAVID_ACH_6,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.DAVID_ACH_THREAD,
    senderId: CONSUMER_IDS.DAVID_WILLIAMS,
    senderType: 'consumer',
    senderName: 'David Williams',
    senderEmail: 'david.williams@williamsrealty.com',
    content:
      "Got the confirmation - thank you both for the quick turnaround! Really appreciate the great customer service. You guys made what could've been a stressful situation very easy.",
    isRead: true,
    daysAgo: 1,
    hoursOffset: 16,
  },

  // ============================================================
  // ALICIA DSCR THREAD
  // ============================================================
  ALICIA_DSCR_1: {
    id: MESSAGE_IDS.ALICIA_DSCR_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ALICIA_DSCR_THREAD,
    senderId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    senderType: 'consumer',
    senderName: 'Alicia Fernandez',
    senderEmail: 'alicia.fernandez@sunbeltdev.com',
    content:
      "Hi, I'm looking at a 4-plex in the Tampa market. The property is cash flowing at $8,200/month gross. What are your current rates for a DSCR loan at 75% LTV?",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 10,
  },
  ALICIA_DSCR_2: {
    id: MESSAGE_IDS.ALICIA_DSCR_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ALICIA_DSCR_THREAD,
    senderId: USER_IDS.RACHEL_MARTINEZ,
    senderType: 'user',
    senderName: 'Rachel Martinez',
    senderEmail: 'rachel.martinez@opsmatrix.local',
    content:
      "Hi Alicia! Great to hear from you again. For a 4-plex in Tampa at 75% LTV with your track record, here's what we're currently offering:\n\n- Rate: 7.75% (30-year fixed)\n- DSCR requirement: 1.25x (your property at $8,200/mo gross should easily qualify)\n- Origination: 1 point\n- Prepay: 3-2-1 structure\n\nWant me to run the numbers for a specific purchase price?",
    isRead: true,
    daysAgo: 1,
    hoursOffset: 9,
  },
  ALICIA_DSCR_3: {
    id: MESSAGE_IDS.ALICIA_DSCR_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.ALICIA_DSCR_THREAD,
    senderId: CONSUMER_IDS.ALICIA_FERNANDEZ,
    senderType: 'consumer',
    senderName: 'Alicia Fernandez',
    senderEmail: 'alicia.fernandez@sunbeltdev.com',
    content:
      "Those rates look competitive! The asking price is $685,000. Can you run the numbers at 75% LTV ($513,750 loan)? I'd also be interested in seeing what rate I could get at 70% LTV.",
    isRead: false,
    daysAgo: 0,
    hoursOffset: 11,
  },

  // ============================================================
  // JENNIFER QUESTIONS THREAD (First-Time Borrower)
  // ============================================================
  JENNIFER_Q_1: {
    id: MESSAGE_IDS.JENNIFER_Q_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.JENNIFER_QUESTIONS_THREAD,
    senderId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    senderType: 'consumer',
    senderName: 'Jennifer Martinez',
    senderEmail: 'jennifer.martinez@gmail.com',
    content:
      "Hi, I'm new to real estate investing and found a great fix & flip opportunity in Austin. The property is listed at $280K and needs about $50K in renovations. I've never done this before - can you explain how the loan process works?",
    isRead: true,
    daysAgo: 10,
    hoursOffset: 13,
  },
  JENNIFER_Q_2: {
    id: MESSAGE_IDS.JENNIFER_Q_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.JENNIFER_QUESTIONS_THREAD,
    senderId: USER_IDS.SARAH_CHEN,
    senderType: 'user',
    senderName: 'Sarah Chen',
    senderEmail: 'sarah.chen@opsmatrix.local',
    content:
      "Hi Jennifer, welcome! I'm excited to help you with your first investment. Here's how our fix & flip loan process works:\n\n1. **Application** - You submit basic info about yourself and the property\n2. **Pre-Approval** - We review your financials and the deal (usually 24-48 hours)\n3. **Property Review** - We order an appraisal to confirm ARV (After Repair Value)\n4. **Underwriting** - Full file review and final approval (3-5 days)\n5. **Closing** - Sign docs and receive funds\n\nFor your deal at $280K + $50K rehab = $330K total, we typically lend up to 85-90% of that. Do you have your renovation scope ready?",
    isRead: true,
    daysAgo: 10,
    hoursOffset: 15,
  },
  JENNIFER_Q_3: {
    id: MESSAGE_IDS.JENNIFER_Q_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.JENNIFER_QUESTIONS_THREAD,
    senderId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    senderType: 'consumer',
    senderName: 'Jennifer Martinez',
    senderEmail: 'jennifer.martinez@gmail.com',
    content:
      "Thank you for the clear explanation! I have a contractor who's putting together the scope of work. A few follow-up questions:\n\n1. What credit score do I need?\n2. How much cash do I need to bring to closing?\n3. How do the renovation draws work?",
    isRead: true,
    daysAgo: 9,
    hoursOffset: 9,
  },
  JENNIFER_Q_4: {
    id: MESSAGE_IDS.JENNIFER_Q_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.JENNIFER_QUESTIONS_THREAD,
    senderId: USER_IDS.SARAH_CHEN,
    senderType: 'user',
    senderName: 'Sarah Chen',
    senderEmail: 'sarah.chen@opsmatrix.local',
    content:
      'Great questions!\n\n1. **Credit**: We work with scores as low as 660, though 680+ gets better rates\n2. **Cash to Close**: Typically 10-15% of total project cost, plus closing costs (~2%)\n3. **Draws**: We hold the rehab funds in escrow. As you complete work, you request a draw. We send an inspector to verify completion, then release funds (usually within 3-5 days)\n\nFor your $330K project, plan for roughly $35-45K cash to close depending on the exact terms.\n\nShall I send you a checklist of documents needed to get started?',
    isRead: true,
    daysAgo: 9,
    hoursOffset: 11,
  },
  JENNIFER_Q_5: {
    id: MESSAGE_IDS.JENNIFER_Q_5,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.JENNIFER_QUESTIONS_THREAD,
    senderId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    senderType: 'consumer',
    senderName: 'Jennifer Martinez',
    senderEmail: 'jennifer.martinez@gmail.com',
    content:
      "Yes please! I'd love to see the document checklist. My credit score is 715 and I have $50K available for this project. Once I get the scope from my contractor, I'll be ready to apply!",
    isRead: true,
    daysAgo: 8,
    hoursOffset: 14,
  },

  // ============================================================
  // JENNIFER DOCS THREAD
  // ============================================================
  JENNIFER_DOC_1: {
    id: MESSAGE_IDS.JENNIFER_DOC_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.JENNIFER_DOCS_THREAD,
    senderId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    senderType: 'consumer',
    senderName: 'Jennifer Martinez',
    senderEmail: 'jennifer.martinez@gmail.com',
    content:
      "I'm looking at the document checklist and have a question - it asks for 2 years of tax returns. I just filed my 2024 return but only have the extension filed for 2023 since I switched accountants. Is that going to be a problem?",
    isRead: true,
    daysAgo: 3,
    hoursOffset: 10,
  },
  JENNIFER_DOC_2: {
    id: MESSAGE_IDS.JENNIFER_DOC_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.JENNIFER_DOCS_THREAD,
    senderId: USER_IDS.MIKE_JOHNSON,
    senderType: 'user',
    senderName: 'Mike Johnson',
    senderEmail: 'mike.johnson@opsmatrix.local',
    content:
      "Hi Jennifer, that's a common situation and not a problem at all! For the 2023 year, you can provide:\n\n1. The extension form (4868)\n2. W-2s or 1099s for that year\n3. Bank statements showing consistent income\n\nThis combination will work for underwriting. Do you have your 2024 return fully filed, or is that also on extension?",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 14,
  },
  JENNIFER_DOC_3: {
    id: MESSAGE_IDS.JENNIFER_DOC_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.JENNIFER_DOCS_THREAD,
    senderId: CONSUMER_IDS.JENNIFER_MARTINEZ,
    senderType: 'consumer',
    senderName: 'Jennifer Martinez',
    senderEmail: 'jennifer.martinez@gmail.com',
    content:
      "2024 is fully filed! I have the complete return ready to upload. I'll gather the 2023 extension and my W-2s from that year. Should I upload everything together or can I do it in batches?",
    isRead: false,
    daysAgo: 1,
    hoursOffset: 9,
  },

  // ============================================================
  // KEVIN LOGIN THREAD
  // ============================================================
  KEVIN_LOGIN_1: {
    id: MESSAGE_IDS.KEVIN_LOGIN_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.KEVIN_LOGIN_THREAD,
    senderId: CONSUMER_IDS.KEVIN_JOHNSON,
    senderType: 'consumer',
    senderName: 'Kevin Johnson',
    senderEmail: 'kevin.johnson@outlook.com',
    content:
      "I'm trying to reset my password but the reset email never arrives. I've checked my spam folder multiple times. My email is kevin.johnson@outlook.com. Can you help?",
    isRead: true,
    daysAgo: 5,
    hoursOffset: 16,
  },
  KEVIN_LOGIN_2: {
    id: MESSAGE_IDS.KEVIN_LOGIN_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.KEVIN_LOGIN_THREAD,
    senderId: USER_IDS.MIKE_JOHNSON,
    senderType: 'user',
    senderName: 'Mike Johnson',
    senderEmail: 'mike.johnson@opsmatrix.local',
    content:
      "Hi Kevin, I looked into this and found that your email was registered with a slight typo - it had an extra period. I've corrected the email address in our system. Could you try the password reset again now?",
    isRead: true,
    daysAgo: 5,
    hoursOffset: 17,
  },
  KEVIN_LOGIN_3: {
    id: MESSAGE_IDS.KEVIN_LOGIN_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.KEVIN_LOGIN_THREAD,
    senderId: CONSUMER_IDS.KEVIN_JOHNSON,
    senderType: 'consumer',
    senderName: 'Kevin Johnson',
    senderEmail: 'kevin.johnson@outlook.com',
    content: "Just tried it and the reset email came through immediately. I'm back in! Thanks for the quick fix.",
    isRead: true,
    daysAgo: 4,
    hoursOffset: 9,
  },
  KEVIN_LOGIN_4: {
    id: MESSAGE_IDS.KEVIN_LOGIN_4,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.KEVIN_LOGIN_THREAD,
    senderId: USER_IDS.MIKE_JOHNSON,
    senderType: 'user',
    senderName: 'Mike Johnson',
    senderEmail: 'mike.johnson@opsmatrix.local',
    content:
      "Great! Glad it's working now. Pro tip: I recommend enabling two-factor authentication for extra security - you can find it in Settings > Security. Let me know if you need anything else!",
    isRead: true,
    daysAgo: 3,
    hoursOffset: 10,
  },

  // ============================================================
  // KEVIN PREQUAL THREAD
  // ============================================================
  KEVIN_PREQUAL_1: {
    id: MESSAGE_IDS.KEVIN_PREQUAL_1,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.KEVIN_PREQUAL_THREAD,
    senderId: CONSUMER_IDS.KEVIN_JOHNSON,
    senderType: 'consumer',
    senderName: 'Kevin Johnson',
    senderEmail: 'kevin.johnson@outlook.com',
    content:
      "Now that my portal access is working, I'd like to get pre-qualified before I start making offers. My annual income is $185K, credit score is 720, and I'm looking at properties in the $200-300K range. What do I need to submit?",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 11,
  },
  KEVIN_PREQUAL_2: {
    id: MESSAGE_IDS.KEVIN_PREQUAL_2,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.KEVIN_PREQUAL_THREAD,
    senderId: USER_IDS.RACHEL_MARTINEZ,
    senderType: 'user',
    senderName: 'Rachel Martinez',
    senderEmail: 'rachel.martinez@opsmatrix.local',
    content:
      "Hi Kevin! With your profile (720 credit, $185K income), you're well-positioned for our investment property loans. For pre-qualification, I'll need:\n\n1. Last 2 months bank statements\n2. Most recent pay stub (or P&L if self-employed)\n3. 2 years tax returns (or 1 year with extension docs for the other)\n4. Valid government ID\n\nOnce I have these, I can issue a pre-qualification letter within 24 hours. The letter is typically valid for 90 days.",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 14,
  },
  KEVIN_PREQUAL_3: {
    id: MESSAGE_IDS.KEVIN_PREQUAL_3,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.KEVIN_PREQUAL_THREAD,
    senderId: CONSUMER_IDS.KEVIN_JOHNSON,
    senderType: 'consumer',
    senderName: 'Kevin Johnson',
    senderEmail: 'kevin.johnson@outlook.com',
    content:
      'Perfect, I have all of those ready. Just uploaded everything to my portal. One question - is the pre-qual specific to a property or can I use it for any property in my target range?',
    isRead: true,
    daysAgo: 1,
    hoursOffset: 10,
  },

  // ============================================================
  // MARIA LLC THREAD (One Sharpe)
  // ============================================================
  MARIA_LLC_1: {
    id: MESSAGE_IDS.MARIA_LLC_1,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARIA_LLC_THREAD,
    senderId: CONSUMER_IDS.MARIA_SANTOS,
    senderType: 'consumer',
    senderName: 'Maria Santos',
    senderEmail: 'maria.santos@santosventures.com',
    content:
      "Hi, I've formed a new LLC (Santos Ventures II LLC) for my next acquisition. I'm uploading the operating agreement and EIN letter now. Can you confirm what else you need for entity verification?",
    isRead: true,
    daysAgo: 4,
    hoursOffset: 10,
  },
  MARIA_LLC_2: {
    id: MESSAGE_IDS.MARIA_LLC_2,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARIA_LLC_THREAD,
    senderId: USER_IDS.SHARPE_ADMIN,
    senderType: 'user',
    senderName: 'Sharpe Admin',
    senderEmail: 'admin@1sharpe.local',
    content:
      'Hi Maria! Great to hear about the new LLC. For entity verification, we need:\n\n1. Operating Agreement (received - thank you!)\n2. EIN Letter (received!)\n3. Certificate of Formation/Organization from the state\n4. Certificate of Good Standing (if the LLC is more than 60 days old)\n\nSince this is a new LLC, just the Certificate of Formation should be sufficient. Do you have that available?',
    isRead: true,
    daysAgo: 3,
    hoursOffset: 11,
  },
  MARIA_LLC_3: {
    id: MESSAGE_IDS.MARIA_LLC_3,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARIA_LLC_THREAD,
    senderId: CONSUMER_IDS.MARIA_SANTOS,
    senderType: 'consumer',
    senderName: 'Maria Santos',
    senderEmail: 'maria.santos@santosventures.com',
    content:
      'Yes, I have the Certificate of Formation! Just uploaded it. The LLC was formed last week in Texas. Should be everything you need now.',
    isRead: true,
    daysAgo: 2,
    hoursOffset: 14,
  },

  // ============================================================
  // MARIA PAYOFF THREAD (One Sharpe)
  // ============================================================
  MARIA_PAYOFF_1: {
    id: MESSAGE_IDS.MARIA_PAYOFF_1,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARIA_PAYOFF_THREAD,
    senderId: CONSUMER_IDS.MARIA_SANTOS,
    senderType: 'consumer',
    senderName: 'Maria Santos',
    senderEmail: 'maria.santos@santosventures.com',
    content:
      "I need a payoff quote for loan #1SC-2023-0456. I have a buyer for the property and we're targeting a close date of February 28th. Can you send me the payoff figures?",
    isRead: true,
    daysAgo: 3,
    hoursOffset: 9,
  },
  MARIA_PAYOFF_2: {
    id: MESSAGE_IDS.MARIA_PAYOFF_2,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARIA_PAYOFF_THREAD,
    senderId: USER_IDS.SHARPE_ADMIN,
    senderType: 'user',
    senderName: 'Sharpe Admin',
    senderEmail: 'admin@1sharpe.local',
    content:
      "Hi Maria, I've generated the payoff quote for loan #1SC-2023-0456:\n\n**Payoff Good Through: February 28, 2026**\n- Principal Balance: $335,890.45\n- Accrued Interest: $4,127.44\n- Per Diem: $78.21/day\n- **Total Payoff: $340,017.89**\n\nWire instructions are attached to this message. Please allow 2 business days for payoff processing after we receive funds.",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 15,
  },
  MARIA_PAYOFF_3: {
    id: MESSAGE_IDS.MARIA_PAYOFF_3,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARIA_PAYOFF_THREAD,
    senderId: CONSUMER_IDS.MARIA_SANTOS,
    senderType: 'consumer',
    senderName: 'Maria Santos',
    senderEmail: 'maria.santos@santosventures.com',
    content:
      "Thank you! I've forwarded the payoff letter and wire instructions to the title company. They'll be wiring funds on the closing date. One question - if closing moves a few days, should I request an updated quote?",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 16,
  },
  MARIA_PAYOFF_4: {
    id: MESSAGE_IDS.MARIA_PAYOFF_4,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.MARIA_PAYOFF_THREAD,
    senderId: USER_IDS.SHARPE_ADMIN,
    senderType: 'user',
    senderName: 'Sharpe Admin',
    senderEmail: 'admin@1sharpe.local',
    content:
      "Good question! If closing moves beyond February 28th, yes please request an updated quote - just message me here. The per diem is $78.21/day, so you can estimate the difference, but we'll need an official updated letter for the title company. Just give me 24 hours notice and I'll have it ready!",
    isRead: true,
    daysAgo: 1,
    hoursOffset: 10,
  },

  // ============================================================
  // CHRIS PHOTOS THREAD (One Sharpe)
  // ============================================================
  CHRIS_PHOTOS_1: {
    id: MESSAGE_IDS.CHRIS_PHOTOS_1,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.CHRIS_PHOTOS_THREAD,
    senderId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    senderType: 'consumer',
    senderName: 'Christopher Davis',
    senderEmail: 'chris.davis@davisflipping.com',
    content:
      "Renovation is complete on 1892 Cedar Lane! I'm uploading the final photos now - kitchen and bathrooms are completely redone. Ready for the final inspection whenever you can schedule it.",
    isRead: true,
    daysAgo: 3,
    hoursOffset: 11,
  },
  CHRIS_PHOTOS_2: {
    id: MESSAGE_IDS.CHRIS_PHOTOS_2,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.CHRIS_PHOTOS_THREAD,
    senderId: USER_IDS.SHARPE_ADMIN,
    senderType: 'user',
    senderName: 'Sharpe Admin',
    senderEmail: 'admin@1sharpe.local',
    content:
      "Great work, Chris! Photos look fantastic - the kitchen transformation is impressive. I'm scheduling the final inspection for this week. Our inspector has the following availability:\n\n- Wednesday 1/22 between 10am-2pm\n- Thursday 1/23 between 9am-12pm\n- Friday 1/24 between 2pm-5pm\n\nWhich works best for you or your contractor to be on-site?",
    isRead: true,
    daysAgo: 2,
    hoursOffset: 14,
  },
  CHRIS_PHOTOS_3: {
    id: MESSAGE_IDS.CHRIS_PHOTOS_3,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.CHRIS_PHOTOS_THREAD,
    senderId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    senderType: 'consumer',
    senderName: 'Christopher Davis',
    senderEmail: 'chris.davis@davisflipping.com',
    content:
      "Thursday morning works perfectly - let's do 10am on the 23rd. My contractor will meet the inspector there. Once we pass, I'll submit the final draw request right away.",
    isRead: true,
    daysAgo: 1,
    hoursOffset: 9,
  },

  // ============================================================
  // CHRIS DRAW THREAD (One Sharpe)
  // ============================================================
  CHRIS_DRAW_1: {
    id: MESSAGE_IDS.CHRIS_DRAW_1,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.CHRIS_DRAW_THREAD,
    senderId: CONSUMER_IDS.CHRISTOPHER_DAVIS,
    senderType: 'consumer',
    senderName: 'Christopher Davis',
    senderEmail: 'chris.davis@davisflipping.com',
    content:
      'Submitting my final draw request - Draw #3 for $18,500. This covers the completion of kitchen and bathroom renovations. All receipts and invoices are uploaded to the portal. Inspection is scheduled for tomorrow at 10am.',
    isRead: true,
    daysAgo: 2,
    hoursOffset: 15,
  },
  CHRIS_DRAW_2: {
    id: MESSAGE_IDS.CHRIS_DRAW_2,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.CHRIS_DRAW_THREAD,
    senderId: USER_IDS.SHARPE_ADMIN,
    senderType: 'user',
    senderName: 'Sharpe Admin',
    senderEmail: 'admin@1sharpe.local',
    content:
      "Draw request received, Chris! I've reviewed the receipts - everything looks in order. Total submitted: $18,500 for:\n\n- Kitchen cabinets & countertops: $8,200\n- Bathroom fixtures & tile: $6,400\n- Appliances: $3,900\n\nPending inspection approval tomorrow, we'll release funds within 3 business days. I'll update you once the inspector's report comes in.",
    isRead: true,
    daysAgo: 1,
    hoursOffset: 10,
  },
  CHRIS_DRAW_3: {
    id: MESSAGE_IDS.CHRIS_DRAW_3,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.CHRIS_DRAW_THREAD,
    senderId: 'system',
    senderType: 'system',
    senderName: '1Sharpe Capital',
    content:
      '**Inspection Complete** - Property at 1892 Cedar Lane has passed final inspection. All renovation work verified complete per approved scope.',
    isRead: true,
    daysAgo: 0,
    hoursOffset: 14,
  },
  CHRIS_DRAW_4: {
    id: MESSAGE_IDS.CHRIS_DRAW_4,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    threadId: MESSAGE_THREAD_IDS.CHRIS_DRAW_THREAD,
    senderId: USER_IDS.SHARPE_ADMIN,
    senderType: 'user',
    senderName: 'Sharpe Admin',
    senderEmail: 'admin@1sharpe.local',
    content:
      "Excellent news - inspection passed! Your final draw of $18,500 has been approved and is processing. Funds will be wired to your account on file within 2-3 business days.\n\nCongratulations on completing another successful project! Let us know when you're ready for the next one.",
    isRead: false,
    daysAgo: 0,
    hoursOffset: 16,
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all message threads as an array
 */
export function getAllMessageThreads(): MessageThreadSeedData[] {
  return Object.values(MESSAGE_THREADS);
}

/**
 * Get all messages as an array
 */
export function getAllMessages(): MessageSeedData[] {
  return Object.values(MESSAGES);
}

/**
 * Get message threads by business
 */
export function getMessageThreadsByBusiness(businessId: string): MessageThreadSeedData[] {
  return Object.values(MESSAGE_THREADS).filter((t) => t.businessId === businessId);
}

/**
 * Get messages by thread
 */
export function getMessagesByThread(threadId: string): MessageSeedData[] {
  return Object.values(MESSAGES)
    .filter((m) => m.threadId === threadId)
    .sort((a, b) => {
      // Sort by daysAgo descending (older first), then by hoursOffset
      if (a.daysAgo !== b.daysAgo) return b.daysAgo - a.daysAgo;
      return (a.hoursOffset || 0) - (b.hoursOffset || 0);
    });
}

/**
 * Get message threads by consumer
 */
export function getMessageThreadsByConsumer(consumerId: string): MessageThreadSeedData[] {
  return Object.values(MESSAGE_THREADS).filter((t) => t.consumerId === consumerId);
}

/**
 * Get unread message count for a consumer
 */
export function getUnreadCountByConsumer(consumerId: string): number {
  return Object.values(MESSAGE_THREADS)
    .filter((t) => t.consumerId === consumerId)
    .reduce((sum, t) => sum + t.unreadCount, 0);
}

/**
 * Calculate timestamp from daysAgo and hoursOffset
 */
export function calculateMessageTimestamp(daysAgo: number, hoursOffset?: number): Date {
  const now = new Date();
  now.setDate(now.getDate() - daysAgo);
  if (hoursOffset !== undefined) {
    now.setHours(hoursOffset, Math.floor(Math.random() * 60), 0, 0);
  }
  return now;
}
